import React, { useState, useEffect } from 'react';
import api from '../services/api';

const REPORT_CONFIGS = [
  { id: 'pnl', name: 'Profit & Loss', slug: 'pnl', params: 'startDate', endParam: 'endDate' },
  { id: 'sales', name: 'Sales', slug: 'sales', params: 'start', endParam: 'end' },
  { id: 'inventory-aging', name: 'Inventory Aging', slug: 'inventory-aging' },
  { id: 'recon-costs', name: 'Recon Costs', slug: 'recon-costs', params: 'startDate', endParam: 'endDate' },
  { id: 'vendor-spend', name: 'Vendor Spend', slug: 'vendor-spend', params: 'startDate', endParam: 'endDate' },
  { id: 'payroll', name: 'Payroll', slug: 'payroll', params: 'startDate', endParam: 'endDate' },
  { id: 'transactions', name: 'Transactions', slug: 'transactions', params: 'startDate', endParam: 'endDate' },
  { id: 'cogs', name: 'Cost of Goods Sold', slug: 'cogs', params: 'start', endParam: 'end' },
  { id: 'operating-expenses', name: 'Operating Expenses', slug: 'operating-expenses', params: 'start', endParam: 'end' },
  { id: 'utilities', name: 'Utilities Analytics', slug: 'utilities', params: 'year' },
  { id: 'rollover', name: 'Rollover Inventory', slug: 'rollover', params: 'as_of_date' },
  { id: 'tax-summary', name: 'Tax Summary', slug: 'tax-summary', params: 'year' },
  { id: 'ar-aging', name: 'AR Aging', slug: 'ar-aging' },
  { id: 'month-summary', name: 'Month Summary', slug: 'month-summary', params: 'month' },
  { id: 'hard-costs', name: 'Hard Costs', slug: 'hard-costs', params: 'start_date', endParam: 'end_date' },
  { id: 'tiv', name: 'Total Invested Value', slug: 'tiv', params: 'start_date', endParam: 'end_date' },
];

const fmt = (n) => typeof n === 'number' ? n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : n;

function ReportDisplay({ report, data }) {
  if (Array.isArray(data)) {
    return (
      <table className="w-full text-left">
        <thead>
          <tr className="border-b border-neutral-300">
            {Object.keys(data[0] || {}).map(k => <th key={k} className="pr-4 py-2 font-medium">{k}</th>)}
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr key={i} className="border-b border-neutral-200">
              {Object.values(row).map((v, j) => <td key={j} className="pr-4 py-1">{v != null ? String(v) : ''}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  // P&L report
  if (report.slug === 'pnl' && data.period) {
    const rev = data.revenue || {};
    const exp = data.expenses || {};
    return (
      <div className="space-y-4">
        <div className="text-neutral-500 text-xs">{data.period.start} — {data.period.end}</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-white p-3 rounded border">
            <div className="text-xs text-neutral-500">Units Sold</div>
            <div className="font-semibold text-lg">{rev.unitsSold ?? 0}</div>
          </div>
          <div className="bg-white p-3 rounded border">
            <div className="text-xs text-neutral-500">Gross Revenue</div>
            <div className="font-semibold text-lg text-green-600">${fmt(rev.grossRevenue)}</div>
          </div>
          <div className="bg-white p-3 rounded border">
            <div className="text-xs text-neutral-500">Total Cost</div>
            <div className="font-semibold text-lg">${fmt(rev.totalCost)}</div>
          </div>
          <div className="bg-white p-3 rounded border">
            <div className="text-xs text-neutral-500">Gross Profit</div>
            <div className={`font-semibold text-lg ${(rev.grossProfit ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>${fmt(rev.grossProfit)}</div>
          </div>
        </div>
        <div>
          <h4 className="font-medium mb-2">Expenses by Category</h4>
          <table className="w-full">
            <thead><tr className="border-b"><th className="text-left py-1">Category</th><th className="text-right py-1">Amount</th></tr></thead>
            <tbody>
              {(exp.byCategory || []).map((r, i) => (
                <tr key={i} className="border-b border-neutral-200"><td className="py-1">{r.category}</td><td className="text-right">${fmt(r.total)}</td></tr>
              ))}
            </tbody>
          </table>
          <div className="mt-2 pt-2 border-t font-medium flex justify-between">
            <span>Total Expenses</span><span>${fmt(exp.total)}</span>
          </div>
        </div>
        <div className="text-lg font-bold pt-2 border-t-2">
          Net Profit: <span className={data.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}>${fmt(data.netProfit)}</span>
        </div>
      </div>
    );
  }

  // Tax summary
  if (report.slug === 'tax-summary' && data.year) {
    return (
      <div className="space-y-3">
        <div className="text-neutral-500">Year: {data.year}</div>
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 bg-white rounded border"><span className="text-neutral-500">Gross Receipts</span><div className="font-semibold">${fmt(data.gross_receipts)}</div></div>
          <div className="p-3 bg-white rounded border"><span className="text-neutral-500">COGS</span><div className="font-semibold">${fmt(data.cogs)}</div></div>
          <div className="p-3 bg-white rounded border"><span className="text-neutral-500">Gross Income</span><div className="font-semibold">${fmt(data.gross_income)}</div></div>
          <div className="p-3 bg-white rounded border"><span className="text-neutral-500">Operating Expenses</span><div className="font-semibold">${fmt(data.total_operating_expenses)}</div></div>
          <div className="p-3 bg-white rounded border col-span-2"><span className="text-neutral-500">Net Profit</span><div className={`font-bold text-lg ${data.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>${fmt(data.net_profit)}</div></div>
        </div>
      </div>
    );
  }

  // Month summary
  if (report.slug === 'month-summary' && data.month) {
    const inc = data.income || {};
    return (
      <div className="space-y-3">
        <div className="text-neutral-500">Month: {data.month}</div>
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 bg-white rounded border"><span className="text-neutral-500">Vehicle Sales</span><div className="font-semibold">${fmt(inc.vehicle_sales)}</div></div>
          <div className="p-3 bg-white rounded border"><span className="text-neutral-500">Doc Fees</span><div className="font-semibold">${fmt(inc.doc_fees)}</div></div>
          <div className="p-3 bg-white rounded border"><span className="text-neutral-500">Total Income</span><div className="font-semibold">${fmt(inc.total)}</div></div>
          <div className="p-3 bg-white rounded border"><span className="text-neutral-500">COGS</span><div className="font-semibold">${fmt(data.total_cogs)}</div></div>
          <div className="p-3 bg-white rounded border"><span className="text-neutral-500">Gross Profit</span><div className="font-semibold text-green-600">${fmt(data.gross_profit)}</div></div>
          <div className="p-3 bg-white rounded border"><span className="text-neutral-500">Operating Expenses</span><div className="font-semibold">${fmt(data.total_operating_expenses)}</div></div>
          <div className="p-3 bg-white rounded border col-span-2"><span className="text-neutral-500">Net Profit</span><div className={`font-bold text-lg ${data.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>${fmt(data.net_profit)}</div></div>
        </div>
      </div>
    );
  }

  // AR aging
  if (report.slug === 'ar-aging' && data.aging_buckets) {
    const b = data.aging_buckets || {};
    return (
      <div className="space-y-4">
        <div className="text-lg font-semibold">Total AR: <span className="text-red-600">${fmt(data.total_ar)}</span></div>
        <div className="space-y-2">
          {['current', '15-21', '22-30', 'over_30'].map(k => (
            <div key={k} className="p-3 bg-white rounded border flex justify-between">
              <span>{k === 'current' ? 'Current' : k.replace('_', ' ')}</span>
              <span className="font-medium">${fmt(b[k]?.total ?? 0)}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Operating expenses (summary + lineItems)
  if (report.slug === 'operating-expenses' && (data.summary || data.lineItems)) {
    return (
      <div className="space-y-4">
        {data.summary?.length > 0 && (
          <div>
            <h4 className="font-medium mb-2">Summary by Category</h4>
            <table className="w-full"><thead><tr className="border-b"><th className="text-left py-1">Category</th><th className="text-right py-1">Total</th></tr></thead>
            <tbody>{data.summary.map((r, i) => <tr key={i} className="border-b"><td className="py-1">{r.category}</td><td className="text-right">${fmt(r.total)}</td></tr>)}</tbody></table>
          </div>
        )}
        {data.lineItems?.length > 0 && (
          <div>
            <h4 className="font-medium mb-2">Line Items</h4>
            <table className="w-full text-xs"><thead><tr className="border-b"><th className="text-left py-1">Date</th><th className="text-left py-1">Description</th><th className="text-left py-1">Category</th><th className="text-right py-1">Amount</th></tr></thead>
            <tbody>{data.lineItems.map((r, i) => <tr key={i} className="border-b"><td className="py-1">{r.date?.split?.('T')[0]}</td><td>{r.description}</td><td>{r.category}</td><td className="text-right">${fmt(r.amount)}</td></tr>)}</tbody></table>
          </div>
        )}
      </div>
    );
  }

  // Vendor spend
  if (report.slug === 'vendor-spend' && data.vendors) {
    return (
      <div className="space-y-2">
        <div className="font-medium">Total Spend: ${fmt(data.summary?.totalSpend ?? data.summary?.total_spend)}</div>
        <table className="w-full"><thead><tr className="border-b"><th className="text-left py-1">Vendor</th><th className="text-right py-1">Total</th></tr></thead>
        <tbody>{(data.vendors || []).map((r, i) => <tr key={i} className="border-b"><td className="py-1">{r.name || r.vendorName || r.vendor_name}</td><td className="text-right">${fmt(r.total_spend ?? r.totalSpend)}</td></tr>)}</tbody></table>
      </div>
    );
  }

  // COGS (vehicles + summary)
  if (report.slug === 'cogs' && (data.vehicles || data.summary)) {
    const sum = data.summary || {};
    return (
      <div className="space-y-4">
        {data.summary && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="p-3 bg-white rounded border"><span className="text-xs text-neutral-500">Purchase</span><div className="font-semibold">${fmt(sum.total_purchase_price)}</div></div>
            <div className="p-3 bg-white rounded border"><span className="text-xs text-neutral-500">Parts</span><div className="font-semibold">${fmt(sum.total_parts_cost)}</div></div>
            <div className="p-3 bg-white rounded border"><span className="text-xs text-neutral-500">Vendor</span><div className="font-semibold">${fmt(sum.total_vendor_cost)}</div></div>
            <div className="p-3 bg-white rounded border"><span className="text-xs text-neutral-500">Total COGS</span><div className="font-semibold">${fmt(sum.total_cogs)}</div></div>
          </div>
        )}
        {data.vehicles?.length > 0 && (
          <div><h4 className="font-medium mb-2">Vehicles</h4>
          <table className="w-full text-xs"><thead><tr className="border-b"><th className="text-left py-1">Stock</th><th className="text-left py-1">Vehicle</th><th className="text-right py-1">Purchase</th><th className="text-right py-1">Parts</th><th className="text-right py-1">Total</th></tr></thead>
          <tbody>{data.vehicles.slice(0, 100).map((v, i) => <tr key={i} className="border-b"><td>{v.stock_number}</td><td>{v.year} {v.make} {v.model}</td><td className="text-right">${fmt(v.purchase_price)}</td><td className="text-right">${fmt(v.parts_cost)}</td><td className="text-right">${fmt(v.total_cost)}</td></tr>)}</tbody></table>
          {data.vehicles.length > 100 && <p className="text-xs text-neutral-500 mt-1">Showing 100 of {data.vehicles.length}</p>}</div>
        )}
      </div>
    );
  }

  // Inventory aging (summary + vehicles)
  if (report.slug === 'inventory-aging' && data.summary) {
    const s = data.summary;
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <div className="p-3 bg-white rounded border"><span className="text-xs text-neutral-500">Total Units</span><div className="font-semibold">{s.totalUnits ?? 0}</div></div>
          <div className="p-3 bg-white rounded border"><span className="text-xs text-neutral-500">Total Value</span><div className="font-semibold">${fmt(s.totalValue)}</div></div>
          <div className="p-3 bg-white rounded border"><span className="text-xs text-neutral-500">Avg Age (days)</span><div className="font-semibold">{s.averageAge?.toFixed?.(0) ?? '-'}</div></div>
        </div>
        {s.buckets?.length > 0 && (
          <div><h4 className="font-medium mb-2">By Age</h4>
          <table className="w-full"><thead><tr className="border-b"><th className="text-left py-1">Range</th><th className="text-right py-1">Count</th><th className="text-right py-1">Value</th></tr></thead>
          <tbody>{s.buckets.map((b, i) => <tr key={i} className="border-b"><td className="py-1">{b.range}</td><td className="text-right">{b.count}</td><td className="text-right">${fmt(b.value)}</td></tr>)}</tbody></table></div>
        )}
        {data.vehicles?.length > 0 && (
          <div><h4 className="font-medium mb-2">Vehicles ({data.vehicles.length})</h4>
          <table className="w-full text-xs"><thead><tr className="border-b"><th className="text-left py-1">Stock</th><th className="text-left py-1">Vehicle</th><th className="text-right py-1">Days</th><th className="text-right py-1">Value</th></tr></thead>
          <tbody>{data.vehicles.slice(0, 50).map((v, i) => <tr key={i} className="border-b"><td>{v.stock_number}</td><td>{v.year} {v.make} {v.model}</td><td className="text-right">{v.days_in_inventory?.toFixed?.(0)}</td><td className="text-right">${fmt(v.purchase_price)}</td></tr>)}</tbody></table>
          {data.vehicles.length > 50 && <p className="text-xs text-neutral-500 mt-1">Showing 50 of {data.vehicles.length}</p>}</div>
        )}
      </div>
    );
  }

  // Utilities analytics
  if (report.slug === 'utilities' && data.summary) {
    const s = data.summary;
    return (
      <div className="space-y-4">
        <div className="text-neutral-500 text-xs">{data.scopeLabel} — {data.year}</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="p-3 bg-white rounded border"><span className="text-xs text-neutral-500">YTD Actual</span><div className="font-semibold">${fmt(s.ytdActual)}</div></div>
          <div className="p-3 bg-white rounded border"><span className="text-xs text-neutral-500">Monthly Run Rate</span><div className="font-semibold">${fmt(s.monthlyRunRate)}</div></div>
          <div className="p-3 bg-white rounded border"><span className="text-xs text-neutral-500">Projected Remainder</span><div className="font-semibold">${fmt(s.projectedRemainderHistorical)}</div></div>
          <div className="p-3 bg-white rounded border"><span className="text-xs text-neutral-500">Full Year Est.</span><div className="font-semibold text-blue-700">${fmt(s.fullYearEstimateHistorical)}</div></div>
        </div>
        {data.byMonth?.length > 0 && (
          <div>
            <h4 className="font-medium mb-2">Monthly Totals</h4>
            <table className="w-full text-sm"><thead><tr className="border-b"><th className="text-left py-1">Month</th><th className="text-right py-1">Total</th><th className="text-right py-1">Unpaid</th></tr></thead>
            <tbody>{data.byMonth.filter(m => m.month <= data.currentMonth).map((m, i) => (
              <tr key={i} className="border-b"><td className="py-1">{m.name}</td><td className="text-right font-mono">${fmt(m.total)}</td><td className="text-right text-amber-600">{m.unpaidCount || '—'}</td></tr>
            ))}</tbody></table>
          </div>
        )}
      </div>
    );
  }

  // Rollover
  if (report.slug === 'rollover' && data.vehicles) {
    return (
      <div className="space-y-2">
        <div className="font-medium">Total: ${fmt(data.total)}</div>
        <table className="w-full"><thead><tr className="border-b"><th className="text-left py-1">Stock</th><th className="text-left py-1">Vehicle</th><th className="text-right py-1">Purchase</th></tr></thead>
        <tbody>{(data.vehicles || []).slice(0, 100).map((v, i) => <tr key={i} className="border-b"><td className="py-1">{v.stock_number}</td><td>{v.year} {v.make} {v.model}</td><td className="text-right">${fmt(v.purchase_price)}</td></tr>)}</tbody></table>
        {(data.vehicles?.length || 0) > 100 && <p className="text-xs text-neutral-500 mt-1">Showing 100 of {data.vehicles.length}</p>}</div>
    );
  }

  // Fallback: key-value table for objects
  const entries = Object.entries(data || {}).filter(([, v]) => v != null && typeof v !== 'object');
  if (entries.length > 0) {
    return (
      <table className="w-full"><tbody>
        {entries.map(([k, v]) => <tr key={k} className="border-b"><td className="py-1 font-medium text-neutral-600">{k}</td><td className="py-1">{String(v)}</td></tr>)}
      </tbody></table>
    );
  }
  return <pre className="whitespace-pre-wrap text-xs">{JSON.stringify(data, null, 2)}</pre>;
}

function ReportViewer({ report, onBack }) {
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(startOfMonth);
  const [endDate, setEndDate] = useState(endOfMonth);
  const [month, setMonth] = useState(now.toISOString().slice(0, 7));
  const [year, setYear] = useState(String(now.getFullYear()));
  const [asOfDate, setAsOfDate] = useState(now.toISOString().split('T')[0]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const buildParams = () => {
    if (!report.params) return {};
    const p = {};
    if (report.params === 'startDate') { p.startDate = startDate; p.endDate = endDate; }
    else if (report.params === 'start') { p.start = startDate; p.end = endDate; }
    else if (report.params === 'start_date') { p.start_date = startDate; p.end_date = endDate; }
    else if (report.params === 'as_of_date') p.as_of_date = asOfDate;
    else if (report.params === 'year') p.year = year;
    else if (report.params === 'month') p.month = month;
    return p;
  };

  const run = async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await api.getReport(report.slug, buildParams());
      setData(res);
    } catch (e) {
      setErr(e.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const needsDateRange = report.params && ['startDate', 'start', 'start_date'].includes(report.params);
  const needsAsOf = report.params === 'as_of_date';
  const needsYear = report.params === 'year';
  const needsMonth = report.params === 'month';
  const needsNoParams = !report.params;

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <button onClick={onBack} className="text-blue-600 hover:text-blue-800 font-medium">← Back</button>
        <h3 className="font-semibold">{report.name}</h3>
      </div>
      <div className="mb-4 flex flex-wrap gap-3 items-end">
        {needsDateRange && (
          <>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-neutral-600">Start</span>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="border rounded px-2 py-1 text-sm" />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-neutral-600">End</span>
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="border rounded px-2 py-1 text-sm" />
            </label>
          </>
        )}
        {needsAsOf && (
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-neutral-600">As of Date</span>
            <input type="date" value={asOfDate} onChange={e => setAsOfDate(e.target.value)} className="border rounded px-2 py-1 text-sm" />
          </label>
        )}
        {needsYear && (
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-neutral-600">Year</span>
            <input type="number" value={year} onChange={e => setYear(e.target.value)} min="2020" max="2030" className="border rounded px-2 py-1 text-sm w-24" />
          </label>
        )}
        {needsMonth && (
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-neutral-600">Month</span>
            <input type="month" value={month} onChange={e => setMonth(e.target.value)} className="border rounded px-2 py-1 text-sm" />
          </label>
        )}
        {(needsDateRange || needsAsOf || needsYear || needsMonth || needsNoParams) && (
          <button onClick={run} disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium">
            {loading ? 'Loading...' : 'Run Report'}
          </button>
        )}
      </div>
      {err && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {err}
          {err.includes('500') || err.toLowerCase().includes('server') ? (
            <p className="mt-2 text-xs">
              The server may be temporarily unavailable. Please try again in a few minutes.
            </p>
          ) : null}
        </div>
      )}
      {data && (
        <div className="bg-neutral-50 rounded-lg p-4 max-h-[70vh] overflow-auto text-sm">
          <ReportDisplay report={report} data={data} />
        </div>
      )}
    </div>
  );
}

function ReportsSection() {
  const [selected, setSelected] = useState(null);
  if (selected) {
    return <ReportViewer report={selected} onBack={() => setSelected(null)} />;
  }
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      {REPORT_CONFIGS.map(r => (
        <button
          key={r.id}
          onClick={() => setSelected(r)}
          className="p-4 rounded-lg border border-neutral-200 hover:border-blue-300 hover:bg-blue-50/50 text-left transition-colors"
        >
          <span className="font-medium text-neutral-800">{r.name}</span>
        </button>
      ))}
    </div>
  );
}

const BackupManager = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('backup'); // 'backup' | 'reports'

  useEffect(() => {
    if (isOpen) {
      setError(null);
      loadData();
    }
  }, [isOpen]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [configData, statusData] = await Promise.all([
        api.getBackupConfig(),
        api.getBackupStatus()
      ]);
      setConfig(configData);
      setStatus(statusData);
    } catch (err) {
      console.error('Failed to load backup data:', err);
      setError(err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const handleRunBackup = async () => {
    if (!window.confirm('Run a full backup now? This may take a minute.')) return;
    
    setRunning(true);
    setLastResult(null);
    try {
      const result = await api.runBackup();
      setLastResult(result);
      await loadData(); // Refresh status
      alert('Backup completed successfully!');
    } catch (error) {
      setLastResult({ success: false, error: error.message });
      alert('Backup failed: ' + error.message);
    } finally {
      setRunning(false);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className={`bg-white w-full rounded-xl shadow-xl max-h-[90vh] overflow-hidden ${activeTab === 'reports' ? 'max-w-4xl' : 'max-w-2xl'}`} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="p-4 border-b border-neutral-200 flex justify-between items-center bg-gradient-to-r from-blue-600 to-blue-700">
          <div>
            <h2 className="text-lg font-bold text-white">Backup & Reports</h2>
            <p className="text-blue-100 text-sm">Weekly automated backups with email delivery</p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white text-2xl">&times;</button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-neutral-200">
          <button
            onClick={() => setActiveTab('backup')}
            className={`flex-1 py-3 px-4 font-medium ${activeTab === 'backup' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-neutral-500 hover:text-neutral-700'}`}
          >
            Backup
          </button>
          <button
            onClick={() => setActiveTab('reports')}
            className={`flex-1 py-3 px-4 font-medium ${activeTab === 'reports' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-neutral-500 hover:text-neutral-700'}`}
          >
            Reports
          </button>
        </div>

        <div className="p-4 overflow-y-auto max-h-[calc(90vh-80px)]">
          {activeTab === 'reports' ? (
            <ReportsSection />
          ) : loading ? (
            <div className="text-center py-8 text-neutral-500">Loading...</div>
          ) : error ? (
            <div className="py-8 text-center">
              <p className="text-red-600 font-medium mb-2">Failed to load</p>
              <p className="text-sm text-neutral-600 mb-4">{error}</p>
              <button onClick={loadData} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Retry</button>
            </div>
          ) : (
            <>
              {/* Configuration Status */}
              <div className="bg-neutral-50 rounded-lg p-4 mb-4">
                <h3 className="font-semibold text-neutral-800 mb-3 flex items-center gap-2">
                  <span className="text-xl">&#9881;</span> Configuration
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-neutral-500">Schedule:</span>
                    <span className="ml-2 font-medium">
                      {config?.schedule && typeof config.schedule === 'object'
                        ? [config.schedule.daily, config.schedule.weekly].filter(Boolean).join('; ')
                        : (typeof config?.schedule === 'string' ? config.schedule : null) || 'Sunday 2:00 AM EST'}
                    </span>
                  </div>
                  <div>
                    <span className="text-neutral-500">Email To:</span>
                    <span className="ml-2 font-medium">{config?.emailTo || 'chris@thewholesalecars.com'}</span>
                  </div>
                  <div>
                    <span className="text-neutral-500">Retention:</span>
                    <span className="ml-2 font-medium">
                      {config?.retention && typeof config.retention === 'object'
                        ? `Daily: ${config.retention.dailyBackups || '30 days'}; Weekly: ${config.retention.weeklyBackups || '12 weeks'}`
                        : (config?.retentionWeeks ?? 52) + ' weeks'}
                    </span>
                  </div>
                  <div>
                    <span className="text-neutral-500">Email Status:</span>
                    <span className={`ml-2 font-medium ${config?.emailConfigured ? 'text-green-600' : 'text-orange-600'}`}>
                      {config?.emailConfigured ? 'Configured' : 'Not Configured'}
                    </span>
                  </div>
                </div>
                {!config?.emailConfigured && (
                  <div className="mt-3 p-2 bg-orange-50 border border-orange-200 rounded text-sm text-orange-700">
                    <strong>Note:</strong> Email is not configured. Set SMTP_USER and SMTP_PASS environment variables to enable email delivery.
                  </div>
                )}
              </div>

              {/* Manual Backup Button */}
              <div className="bg-blue-50 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-blue-800">Run Backup Now</h3>
                    <p className="text-sm text-blue-600">Generate PDF report, CSV exports, and database backup</p>
                  </div>
                  <button
                    onClick={handleRunBackup}
                    disabled={running}
                    className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                      running 
                        ? 'bg-neutral-300 text-neutral-500 cursor-not-allowed' 
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {running ? (
                      <span className="flex items-center gap-2">
                        <span className="animate-spin">&#9696;</span> Running...
                      </span>
                    ) : (
                      'Run Backup'
                    )}
                  </button>
                </div>
              </div>

              {/* Last Result */}
              {lastResult && (
                <div className={`rounded-lg p-4 mb-4 ${lastResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  <h3 className={`font-semibold ${lastResult.success ? 'text-green-800' : 'text-red-800'}`}>
                    {lastResult.success ? 'Backup Completed' : 'Backup Failed'}
                  </h3>
                  {lastResult.success ? (
                    <div className="text-sm text-green-700 mt-2">
                      <p>Duration: {lastResult.duration}s</p>
                      <p>Email Sent: {lastResult.emailSent ? 'Yes' : 'No (check email config)'}</p>
                      <p className="mt-2 font-medium">Files Generated:</p>
                      <ul className="list-disc list-inside">
                        <li>{lastResult.files?.pdf}</li>
                        {lastResult.files?.csvs?.map((csv, i) => <li key={i}>{csv}</li>)}
                        {lastResult.files?.database && <li>{lastResult.files.database}</li>}
                      </ul>
                    </div>
                  ) : (
                    <p className="text-sm text-red-700 mt-2">{lastResult.error}</p>
                  )}
                </div>
              )}

              {/* Backup History */}
              <div className="bg-neutral-50 rounded-lg p-4">
                <h3 className="font-semibold text-neutral-800 mb-3 flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <span className="text-xl">&#128451;</span> Backup History
                  </span>
                  <span className="text-sm font-normal text-neutral-500">
                    {status?.totalFiles || 0} files ({status?.totalSizeMB || 0} MB)
                  </span>
                </h3>
                
                {status?.backups && Object.keys(status.backups).length > 0 ? (
                  <div className="space-y-3 max-h-60 overflow-y-auto">
                    {Object.entries(status.backups).slice(0, 10).map(([date, files]) => (
                      <div key={date} className="border-b border-neutral-200 pb-2">
                        <div className="font-medium text-neutral-700 text-sm mb-1">{date}</div>
                        <div className="flex flex-wrap gap-2">
                          {files.map((file, i) => (
                            <span 
                              key={i}
                              className={`text-xs px-2 py-1 rounded ${
                                file.type === 'pdf' ? 'bg-red-100 text-red-700' :
                                file.type === 'csv' ? 'bg-green-100 text-green-700' :
                                file.type === 'database' ? 'bg-purple-100 text-purple-700' :
                                'bg-neutral-100 text-neutral-700'
                              }`}
                            >
                              {file.filename} ({formatBytes(file.size)})
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-neutral-500 text-center py-4">No backups yet</p>
                )}
              </div>

              {/* What's Included */}
              <div className="mt-4 p-4 bg-neutral-50 rounded-lg">
                <h3 className="font-semibold text-neutral-800 mb-2">What's Included in Each Backup:</h3>
                <div className="grid grid-cols-2 gap-2 text-sm text-neutral-600">
                  <div className="flex items-center gap-2">
                    <span className="text-green-500">&#10003;</span> PDF Summary Report
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-green-500">&#10003;</span> All Vehicle VINs & Details
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-green-500">&#10003;</span> Complete Expense History
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-green-500">&#10003;</span> Bank Account Balances
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-green-500">&#10003;</span> Repair Order Records
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-green-500">&#10003;</span> Parts Inventory
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-green-500">&#10003;</span> Vendor List
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-green-500">&#10003;</span> Full Database Backup
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BackupManager;
